<template>
  <!-- This is new version of McTimelineHourly and McTimelineDaily -->
  <div
    ref="timeline-table-daily-ref"
    class="timeline-table-daily"
    :style="`max-height: ${containerHeight}px`"
  >
    <!-- Sticky Header in First Row and First Col -->
    <div class="timeline-table-daily__sticky-header-row">
      <div class="timeline-table-daily__sticky-header-col">
        <div class="timeline-table-daily__sticky-header-container">Plat Nomor</div>
      </div>
    </div>

    <!-- Scroll Header and Body -->
    <div class="timeline-table-daily__grid-scroll" ref="scroll-container-ref">
      <!-- Header -->
      <div class="timeline-table-daily__grid-scroll-header">
        <div class="timeline-table-daily__sticky-header-col"></div>

        <div class="timeline-table-daily__header-cols-scroll-x">
          <!-- Header contain time info -->
          <div
            v-for="(timeLabel, index) in timeLabels"
            class="timeline-table-daily__header-cols"
            :key="`timelabel-${index}`"
          >
            <div class="timeline-table-daily__header-col">
              {{ timeLabel }}
            </div>
          </div>
        </div>
      </div>

      <!-- Body -->
      <div class="timeline-table-daily__grid-scroll-body">
        <!-- First col that sticky -->
        <div class="timeline-table-daily__first-sticky-cols">
          <div class="timeline-table-daily__first-sticky-cols-container">
            <!-- If there is no data -->
            <div v-if="resources.length == 0" class="timeline-table-daily__first-sticky-col">
              <div class="timeline-table-daily__first-sticky-col-container">
                <div class="timeline-table-daily__resource-value">
                  {{ $t('fleetAvailability.others.noData') }}
                </div>
              </div>
            </div>

            <div
              v-for="(resource, index) in resources"
              class="timeline-table-daily__first-sticky-col"
              :style="{ minHeight: `${resource.rowSpan * 6.25}rem` }"
              :key="`resource-${index}`"
            >
              <div class="timeline-table-daily__first-sticky-col-container">
                <div class="timeline-table-daily__resource-value">
                  {{ resource.resourceValue }}
                </div>
                <i v-if="resource.rowSpan > 1" class="icon-fill_alert-triangle" />
              </div>
            </div>
          </div>
        </div>

        <div class="timeline-table-daily__grid-scroll-rows">
          <div class="current-time" ref="current-time"></div>

          <template v-if="resources.length == 0">
            <div class="timeline-table-daily__grid-scroll-cols">
              <div class="timeline-table-daily__event-rows">
                <div class="timeline-table-daily__event-row"></div>
              </div>

              <div
                v-for="(_, index) in timeLabels"
                class="timeline-table-daily__grid-scroll-col"
                :key="`timelabel2-${index}`"
              >
                <div class="timeline-table-daily__grid-scroll-col-grow"></div>
              </div>
            </div>
          </template>

          <div
            v-for="(resource, index) in resources"
            class="timeline-table-daily__grid-scroll-cols"
            :key="`resource2-${index}`"
          >
            <!-- Event Rows -->
            <div class="timeline-table-daily__event-rows">
              <template v-if="resource.timelines.length === 0">
                <div class="timeline-table-daily__event-row"></div>
              </template>
              <template v-else>
                <div
                  v-for="(timeline, index) in resource.timelines"
                  class="timeline-table-daily__event-row"
                  :key="`timeline3-${index}`"
                >
                  <template v-for="event in timeline">
                    <EventItemDaily
                      :startTime="event.start"
                      :endTime="event.end"
                      :color="colorMappings[event.status.name]"
                      :title="event.number"
                      :contents="event.rows"
                      :key="`event-item-${event.id}`"
                      :id="`popover-target-${event.id}`"
                      @click="() => handleClickEvent(event)"
                      @hover="(e) => handleHoverEvent(e, event)"
                    />

                    <b-popover
                      no-fade
                      custom-class="popover-event-item"
                      :key="`popover-${event.id}`"
                      :target="`popover-target-${event.id}`"
                      :ref="`popover-${event.id}-ref`"
                    >
                      <template #title>{{ event.number }}</template>
                      <template #default>
                        <div
                          class="popover-row"
                          v-for="(content, index) in event.rows"
                          v-html="content"
                          :key="`popover-row-${index}`"
                        ></div>
                      </template>
                    </b-popover>
                  </template>
                </div>
              </template>
            </div>

            <!-- Cols contain time info -->
            <div
              v-for="(_, index) in timeLabels"
              class="timeline-table-daily__grid-scroll-col"
              :style="{ minHeight: `${resource.rowSpan * 6.25}rem` }"
              :key="`timelabel3-${index}`"
            >
              <div class="timeline-table-daily__grid-scroll-col-grow"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue'
import moment from 'moment'
import EventItemDaily from '../EventItemDailyV2.vue'
import { fleetDestination } from '../../lib/constants/fleet-destination'

export default {
  components: {
    EventItemDaily,
  },
  provide() {
    return {
      startWeekDay: computed(() => this.startWeekDay),
      endWeekDay: computed(() => this.endWeekDay),
    }
  },
  inject: ['timeSelected', 'periodLength'],
  props: {
    resources: {
      type: Array,
    },
    resourceField: {
      type: Object,
    },
    colorMappings: {
      type: Object,
    },
  },
  data() {
    return {
      containerHeight: 0,
      updatePointerInterval: null,
    }
  },
  computed: {
    timeLabels() {
      let labels = []

      for (let i = 0; i < this.periodLength; i++) {
        let formattedDay = moment(this.timeSelected[0])
          .add(i, 'd')
          .locale(this.$i18n.locale)
          .format('dddd DD')

        labels.push(formattedDay)
      }

      return labels
    },
    startWeekDay() {
      return moment(this.timeSelected[0])
    },
    endWeekDay() {
      return moment(this.timeSelected[1])
    },
  },
  watch: {
    timeSelected() {
      this.setCurrentTimePointer()
    },
    resourceField() {
      this.scrollToCurrentTime()
    },
  },
  mounted() {
    const containerSize = this.$refs['timeline-table-daily-ref'].getBoundingClientRect()
    this.containerHeight = window.innerHeight - (containerSize.top + 40)
    this.setCurrentTimePointer()
  },
  beforeDestroy() {
    clearInterval(this.updatePointerInterval)
  },
  methods: {
    scrollToCurrentTime() {
      const scrollContainer = this.$refs['scroll-container-ref']
      const currentTimePointer = this.$refs['current-time']

      // Calculate the target scroll position to center the content
      const containerWidth = scrollContainer.clientWidth
      const targetScrollPosition = currentTimePointer.offsetLeft - containerWidth / 2

      // Scroll to the calculated position
      scrollContainer.style.scrollBehavior = 'smooth'
      scrollContainer.scrollLeft = targetScrollPosition

      // Set back the scroll behavior to auto
      setTimeout(() => {
        scrollContainer.style.scrollBehavior = 'auto'
      }, 1000)
    },
    setCurrentTimePointer() {
      const isSameOrAfterStart = moment(new Date()).isSameOrAfter(this.startWeekDay, 'd')
      const isSameOrBeforeEnd = moment(new Date()).isSameOrBefore(this.endWeekDay, 'd')
      if (!isSameOrAfterStart || !isSameOrBeforeEnd) {
        this.$refs['current-time'].style.display = 'none'
        return
      }

      const currentTimePointer = this.$refs['current-time']
      const startWeekDay = this.startWeekDay.clone().startOf('day')
      const endWeekDay = this.endWeekDay.clone().add(1, 'd').startOf('day')
      const totalWidthInSeconds = endWeekDay.diff(startWeekDay, 'seconds')

      currentTimePointer.style.display = 'block'

      const updatePointer = () => {
        const currentTime = moment()
        const positionInSeconds = currentTime.diff(startWeekDay, 'seconds')

        currentTimePointer.style.left = `${(positionInSeconds / totalWidthInSeconds) * 100}%`
      }

      updatePointer()

      clearInterval(this.updatePointerInterval)
      this.updatePointerInterval = setInterval(updatePointer, 10000)

      // scroll to the current time
      this.scrollToCurrentTime()
    },
    handleClickEvent(eventItem) {
      // TODO: refactor this logic if the event isn't a fleet task
      if (!fleetDestination[eventItem.type]) return

      this.$router.push({ name: fleetDestination[eventItem.type], params: { id: eventItem.id } })
    },
    handleHoverEvent(elementEvent, eventItem) {
      const eventItemElement = elementEvent.target

      // Clone the event item node and set width to auto
      const eventItemElementCloned = eventItemElement.cloneNode(true)
      eventItemElementCloned.style.width = 'auto'
      eventItemElementCloned.style.zIndex = '-1'

      eventItemElement.after(eventItemElementCloned)

      // get the width of ori element and clone element
      const actualWidth = eventItemElement.offsetWidth
      const overflowWidth = eventItemElementCloned.offsetWidth

      // set boolean variable to detect overflow in item
      const isOverflow = overflowWidth > actualWidth

      eventItemElementCloned.remove()

      // return if it's overflow
      if (!isOverflow) return

      // open popover if it's entering the el
      if (elementEvent.type == 'mouseenter') {
        this.$refs[`popover-${eventItem.id}-ref`][0].$emit('open')
      }

      // close popover if it's leaving the el
      if (elementEvent.type == 'mouseleave') {
        this.$refs[`popover-${eventItem.id}-ref`][0].$emit('close')
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import './style.scss';
</style>
